import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ErrorStateMatcher, MatCheckboxChange, MatSelectChange } from '@angular/material';
import cloneDeep from 'lodash-es/cloneDeep';
import { TranslocoService } from '@ngneat/transloco';
import { untilDestroyed } from 'ngx-take-until-destroy';
import * as Papa from 'papaparse/papaparse.js';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { StaffService } from '../../../core/api/staff.service';
import { FormErrorScrollingService } from '../../../shared/form-error-scrolling/form-error-scrolling.service';
import { PatientFormGroup } from '../../../shared/patient-form/patient-form.model';
import { CSVPatientsFormGroup } from './csv-patients-form-group.model';
import {
  ImportWizardFileReceivedService,
  PatientAttribute
} from './import-wizard-file-received.service';

@Component({
  selector: 'csi-import-wizard-file-received',
  templateUrl: './import-wizard-file-received.component.html',
  styleUrls: ['./import-wizard-file-received.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ImportWizardFileReceivedComponent implements OnDestroy, OnInit {
  @Input() file$: Observable<File[]>;
  @Input() csvPatientsFormGroup: CSVPatientsFormGroup;
  @Output() successfulValidation: EventEmitter<null> = new EventEmitter();
  // Emitted instead of blocking in place when validation finds flagged rows — the wizard
  // responds by revealing the dedicated review step where the user fixes or skips them.
  @Output() reviewFlaggedRows: EventEmitter<null> = new EventEmitter();
  @Output() goBack: EventEmitter<null> = new EventEmitter();
  @Output() cancel: EventEmitter<null> = new EventEmitter();
  @ViewChild('tableContainer', { static: false }) tableContainer: ElementRef;
  protected readonly patientAttributeSynonyms = Array.from(
    this.importWizardFileReceivedService.patientAttributeMappings.keys()
  );
  public readonly hasHeader = true;
  protected readonly PatientAttribute = PatientAttribute;
  protected patientsColumns: string[];
  public patients: string[][];
  protected matSelectors: FormGroup;
  protected validPatientValues: boolean[][] = [];
  public matErrorMissingColumn: string;
  public matErrorInvalidValue: string;
  protected errorStateMatcher: ErrorStateMatcher = {
    isErrorState: () => !!this.matErrorMissingColumn
  };
  public csvValidationError: string | null;
  public largeFileWarning: string | null = null;
  public processedRowCount = 0;
  public totalRowCount = 0;
  public isBuildingPatients = false;
  public isValidatingPatients = false;
  public displayedPatients: string[][] = [];
  public hiddenRowCount = 0;
  public readonly previewRowLimit = 100;
  // Above this row count the wizard still processes the file but the build, dedupe and create
  // phases all become noticeably slower; warn the user up front so they know what to expect.
  public readonly largeFileWarningRowThreshold = 10000;
  private rawCSVData: string[][];
  private buildGeneration = 0;
  private readonly chunkSize = 500;

  constructor(
    private formBuilder: FormBuilder,
    private changeDetectorRef: ChangeDetectorRef,
    private importWizardFileReceivedService: ImportWizardFileReceivedService,
    private formErrorScrollingService: FormErrorScrollingService,
    private staffService: StaffService,
    private translocoService: TranslocoService
  ) {}

  ngOnInit() {
    this.file$
      .pipe(
        switchMap(files =>
          files[0] ? this.importWizardFileReceivedService.removeCharFromFile(files[0]) : of()
        ),
        untilDestroyed(this)
      )
      .subscribe(modifiedFile => {
        if (!!modifiedFile) {
          Papa.parse(modifiedFile, {
            complete: results => {
              this.rawCSVData = results.data.filter(data => {
                return (
                  data
                    .toString()
                    .replace(/,/g, '')
                    .trim() !== ''
                );
              });
              this.processCSVResults(this.hasHeader);
            }
          });
        }
      });
  }

  ngOnDestroy() {}

  public fileHeaderOptionChange(containsHeader: MatCheckboxChange) {
    this.processCSVResults(containsHeader.checked);
  }

  public validatePatients() {
    if (this.isValidatingPatients || this.isBuildingPatients) {
      return;
    }
    // Paint the loading state immediately, then defer the synchronous validation work one
    // tick so the spinner is on screen before getValidEntries + updateThisAndDescendants
    // monopolize the main thread on large imports.
    this.isValidatingPatients = true;
    this.changeDetectorRef.markForCheck();
    setTimeout(() => this.runPatientValidation(), 0);
  }

  private runPatientValidation() {
    this.validPatientValues = this.importWizardFileReceivedService.getValidEntries(
      this.csvPatientsFormGroup.controls.patients.controls as PatientFormGroup[],
      this.matSelectors
    );

    this.csvPatientsFormGroup.updateThisAndDescendants();
    this.setMatErrors();
    const passesValidation =
      this.csvPatientsFormGroup.controls.patients.valid &&
      !this.csvValidationError &&
      this.matSelectors.valid;
    this.isValidatingPatients = false;
    this.changeDetectorRef.markForCheck();
    if (passesValidation) {
      this.successfulValidation.emit(null);
      return;
    }
    // invalidColumns is set by the same shared predicate flaggedPatientRows uses, and it was
    // just recomputed by updateThisAndDescendants above — an O(1) read instead of re-walking
    // every control of every row. NOTE: the error VALUE is an empty string, so this must be a
    // `!= null` presence check (like setMatErrors below) — hasError() truthiness-tests the value
    // and would always return false here.
    const patientsErrors = this.csvPatientsFormGroup.controls.patients.errors;
    const hasFlaggedRows =
      !this.csvValidationError &&
      this.matSelectors.valid &&
      !!patientsErrors &&
      patientsErrors['invalidColumns'] != null;
    if (hasFlaggedRows) {
      // Rows themselves are the problem (not the CSV shape or the column mapping): hand off to
      // the dedicated review step where the user fixes or skips each flagged row.
      this.reviewFlaggedRows.emit(null);
      return;
    }
    if (!!this.tableContainer) {
      this.formErrorScrollingService.scrollToFirstInvalidControlInView(
        this.tableContainer,
        undefined,
        { isHostMatTable: true, isHostScrollable: true }
      );
    }
  }

  protected changeMatSelector(matSelectChange: MatSelectChange, columnIndex: number) {
    this.importWizardFileReceivedService.removeDuplicateOptions(
      matSelectChange.value,
      columnIndex,
      this.matSelectors
    );
    this.mapCSVPatientsToPatientFormGroups();
  }

  private setMatErrors() {
    this.matErrorInvalidValue = '';
    this.matErrorMissingColumn = '';
    if (
      this.matSelectors &&
      this.matSelectors.errors &&
      this.matSelectors.errors['missingRequired'] != null
    ) {
      this.matErrorMissingColumn = this.translocoService.translate(
        'Patient Last Name, First Name, and one of Patient Email, Phone, or Birthday are required in the header.'
      );
    }
    if (
      this.csvPatientsFormGroup.controls.patients.errors &&
      this.csvPatientsFormGroup.controls.patients.errors['invalidColumns'] != null
    ) {
      this.matErrorInvalidValue = this.translocoService.translate(
        'Cells contain invalid values. Please fix before continuing.'
      );
    }
  }

  private processCSVResults(header: boolean) {
    const csvDataClone = cloneDeep(this.rawCSVData).filter(
      row => row.length !== 1 || row[0] !== ''
    );
    this.patients = csvDataClone.splice(header ? 1 : 0);
    // Data row 0 sits on spreadsheet row 2 when the file has a header row; the flagged-row
    // review uses this offset so its "Row" column matches the user's file.
    this.csvPatientsFormGroup.csvRowNumberOffset = header ? 2 : 1;
    this.displayedPatients = this.patients.slice(0, this.previewRowLimit);
    this.hiddenRowCount = Math.max(0, this.patients.length - this.previewRowLimit);
    this.patientsColumns = header
      ? csvDataClone[0]
      : this.patients[0].map((val, index) => index.toString());
    this.csvValidationError = this.importWizardFileReceivedService.csvValidator(
      this.patients,
      this.patientsColumns
    );
    this.largeFileWarning =
      this.patients.length > this.largeFileWarningRowThreshold
        ? this.translocoService.translate('This file has') +
          ' ' +
          this.patients.length +
          ' ' +
          this.translocoService.translate(
            'rows. The wizard will process the whole file, but large imports take several minutes and use significant browser memory. For the fastest experience consider splitting into chunks of 5,000 to 10,000 rows.'
          )
        : null;
    this.changeDetectorRef.markForCheck();

    if (this.csvValidationError) {
      return;
    }
    this.buildMatSelectorForms();
    this.validPatientValues = [];
    this.mapCSVPatientsToPatientFormGroups();
  }

  private mapCSVPatientsToPatientFormGroups() {
    this.csvPatientsFormGroup.reset();
    while (this.csvPatientsFormGroup.controls.patients.controls.length !== 0) {
      this.csvPatientsFormGroup.controls.patients.controls.pop();
    }
    // Bumping the generation cancels any in-flight chunk loop from a previous file/header change.
    const currentGeneration = ++this.buildGeneration;
    this.totalRowCount = this.patients.length;
    this.processedRowCount = 0;
    this.isBuildingPatients = true;
    this.changeDetectorRef.markForCheck();

    this.staffService
      .getLoggedInStaff()
      .pipe(untilDestroyed(this))
      .subscribe(loggedInDoctor => {
        if (currentGeneration !== this.buildGeneration) {
          return;
        }
        const loggedInDoctorId = loggedInDoctor && loggedInDoctor.id ? loggedInDoctor.id : '';
        this.buildPatientChunksFromIndex(loggedInDoctorId, currentGeneration, 0);
      });
  }

  private buildPatientChunksFromIndex(
    loggedInDoctorId: string,
    generationToken: number,
    startIndex: number
  ) {
    if (generationToken !== this.buildGeneration) {
      return;
    }
    if (startIndex >= this.patients.length) {
      this.csvPatientsFormGroup.updateThisAndDescendants();
      this.isBuildingPatients = false;
      this.changeDetectorRef.markForCheck();
      return;
    }
    const endIndex = Math.min(startIndex + this.chunkSize, this.patients.length);
    const rowChunk = this.patients.slice(startIndex, endIndex);
    const patientFormGroups = this.importWizardFileReceivedService.csvRowsToPatientFormGroups(
      rowChunk,
      this.patientsColumns,
      this.matSelectors,
      loggedInDoctorId
    );
    // Stamp each group with its original CSV data-row index so the flagged-row review reports
    // stable row numbers even after rows are removed from the import.
    patientFormGroups.forEach(
      (patientFormGroup, chunkRowIndex) =>
        (patientFormGroup.csvImportRowIndex = startIndex + chunkRowIndex)
    );
    this.csvPatientsFormGroup.controls.patients.controls.push(...patientFormGroups);
    this.processedRowCount = endIndex;
    this.changeDetectorRef.markForCheck();
    // Yield to the event loop so the UI stays responsive while building the next chunk.
    setTimeout(
      () => this.buildPatientChunksFromIndex(loggedInDoctorId, generationToken, endIndex),
      0
    );
  }

  private buildMatSelectorForms() {
    const controlsConfig = this.importWizardFileReceivedService.createControlsConfig(
      this.patientsColumns
    );
    this.matSelectors = this.formBuilder.group(controlsConfig, {
      validators: [this.importWizardFileReceivedService.createControlsValidator()]
    });
  }
}
